/**
 * F1.4 — szlovák DPH számítás és alapértelmezés.
 * vat_code: '23' | '19' | '5' | '0' | 'non_vat'
 * [feltételezés] amount / amount_gross = bruttó (kanonikus tárolt összeg).
 * amount_basis: a felhasználó által megadott összeg értelmezése.
 */

export type VatCode = "23" | "19" | "5" | "0" | "non_vat";

export const VAT_CODES: VatCode[] = ["23", "19", "5", "0", "non_vat"];

/** Felvitt összeg: s DPH (gross), bez DPH (net), fekete számla (black ≈ net/non_vat). */
export type AmountBasis = "gross" | "net" | "black";

export const AMOUNT_BASIS_OPTIONS: AmountBasis[] = ["gross", "net", "black"];

/** Új kiadás űrlap: csak s DPH / bez DPH. A fekete számla külön jelző. */
export const ENTRY_AMOUNT_BASIS = ["gross", "net"] as const;

export const AMOUNT_BASIS_LABEL: Record<AmountBasis, string> = {
  gross: "s DPH",
  net: "bez DPH",
  black: "Fekete számla"
};

export function isAmountBasis(v: string | null | undefined): v is AmountBasis {
  return v === "gross" || v === "net" || v === "black";
}

export function isVatCode(v: string | null | undefined): v is VatCode {
  return v === "23" || v === "19" || v === "5" || v === "0" || v === "non_vat";
}

export function vatRateFromCode(code: VatCode): number {
  if (code === "non_vat" || code === "0") return 0;
  return Number(code);
}

export function vatCodeFromRate(rate: number | null | undefined): VatCode {
  if (rate == null) return "23";
  if (rate === 0) return "0";
  if (rate === 5) return "5";
  if (rate === 19) return "19";
  if (rate === 23) return "23";
  return "23";
}

export type VatBreakdown = {
  vat_code: VatCode;
  vat_rate: number;
  amount_net: number;
  vat_amount: number;
  amount_gross: number;
};

/** Bruttóból nettó + ÁFA. */
export function breakdownFromGross(gross: number, code: VatCode): VatBreakdown {
  const g = Number.isFinite(gross) ? gross : 0;
  const rate = vatRateFromCode(code);
  if (code === "non_vat" || rate === 0) {
    return {
      vat_code: code,
      vat_rate: 0,
      amount_net: round2(g),
      vat_amount: 0,
      amount_gross: round2(g)
    };
  }
  const net = g / (1 + rate / 100);
  const vat = g - net;
  return {
    vat_code: code,
    vat_rate: rate,
    amount_net: round2(net),
    vat_amount: round2(vat),
    amount_gross: round2(g)
  };
}

/** Nettóból bruttó + ÁFA. */
export function breakdownFromNet(net: number, code: VatCode): VatBreakdown {
  const n = Number.isFinite(net) ? net : 0;
  const rate = vatRateFromCode(code);
  if (code === "non_vat" || rate === 0) {
    return {
      vat_code: code,
      vat_rate: 0,
      amount_net: round2(n),
      vat_amount: 0,
      amount_gross: round2(n)
    };
  }
  const gross = n * (1 + rate / 100);
  return {
    vat_code: code,
    vat_rate: rate,
    amount_net: round2(n),
    vat_amount: round2(gross - n),
    amount_gross: round2(gross)
  };
}

const FEE_TITLE_RE =
  /illeték|illetek|kataszter|kataster|poplatok|správny poplatok|spravny poplatok|notár|notar|magánszemély|maganszemely|súkromn|sukromn/i;

/**
 * Alapértelmezett DPH kód új kiadáshoz.
 * Üres kategóriánál null — nem esik vissza csendben 23%-ra.
 * Illeték, nem DPH-alany kontakt és (ismeretlen) munkadíj → non_vat.
 * Anyag / eszköz / szolgáltatás, vagy igazolt DPH-alany → 23.
 */
export function defaultVatCode(opts: {
  category?: string | null | undefined;
  contactIsVatPayer?: boolean | null | undefined;
  title?: string | null | undefined;
  isBlackInvoice?: boolean | undefined;
}): VatCode | null {
  if (opts.isBlackInvoice) return "non_vat";
  if (opts.contactIsVatPayer === false) return "non_vat";
  if (opts.category === "permit") return "non_vat";
  if (opts.title && FEE_TITLE_RE.test(opts.title) && opts.contactIsVatPayer !== true) return "non_vat";
  if (opts.category === "labor" && opts.contactIsVatPayer !== true) return "non_vat";
  if (opts.category === "material" || opts.category === "equipment" || opts.category === "service") return "23";
  if (opts.contactIsVatPayer === true) return "23";
  return null;
}

const SUSPICIOUS_TITLE_RE =
  /illeték|kataszter|kataster|munkadíj|poplatok|správny poplatok|notár|notar|magánszemély|súkromná/i;

export type SuspiciousVatReason =
  | "permit_category"
  | "title_hint"
  | "contact_non_vat"
  | "always_23";

export function suspiciousVatReasons(row: {
  category?: string | null;
  title?: string | null;
  vat_code?: string | null;
  vat_rate?: number | null;
  contactIsVatPayer?: boolean | null;
}): SuspiciousVatReason[] {
  const reasons: SuspiciousVatReason[] = [];
  const code = row.vat_code ?? vatCodeFromRate(row.vat_rate);
  if (row.category === "permit" && code !== "0" && code !== "non_vat") {
    reasons.push("permit_category");
  }
  if (row.title && SUSPICIOUS_TITLE_RE.test(row.title) && code === "23") {
    reasons.push("title_hint");
  }
  if (row.contactIsVatPayer === false && code !== "non_vat") {
    reasons.push("contact_non_vat");
  }
  if (code === "23" && (row.category === "permit" || (row.title && SUSPICIOUS_TITLE_RE.test(row.title)))) {
    if (!reasons.includes("always_23")) reasons.push("always_23");
  }
  return reasons;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export type ExpenseAmountFields = VatBreakdown & {
  amount: number;
  amount_basis: AmountBasis;
};

/**
 * Felvitt összeg + típus → tárolandó amount / nettó / bruttó / ÁFA mezők.
 * - gross (s DPH): a megadott összeg bruttó
 * - net (bez DPH): a megadott összeg nettó → bruttó számítás
 * - black (fekete): mint bez DPH non_vat (nettó = bruttó, ÁFA = 0)
 * A kanonikus `amount` mindig a bruttó (feketénél = a megadott összeg).
 */
export function resolveExpenseAmounts(opts: {
  entered: number;
  basis: AmountBasis;
  vatCode?: string | null;
  category?: string | null;
  title?: string | null;
  contactIsVatPayer?: boolean | null;
  isBlackInvoice?: boolean;
}): ExpenseAmountFields {
  const black = opts.basis === "black" || opts.isBlackInvoice === true;
  const basis: AmountBasis = black ? "black" : opts.basis;
  const code: VatCode = black
    ? "non_vat"
    : isVatCode(opts.vatCode)
      ? opts.vatCode
      : (defaultVatCode({
          category: opts.category ?? null,
          title: opts.title ?? null,
          contactIsVatPayer: opts.contactIsVatPayer ?? null
        }) ?? "23");

  const breakdown =
    basis === "net" || basis === "black"
      ? breakdownFromNet(opts.entered, code)
      : breakdownFromGross(opts.entered, code);

  return {
    ...breakdown,
    amount: breakdown.amount_gross,
    amount_basis: basis
  };
}

/** Szerkesztő mező: net/black esetén a nettót mutatjuk, különben a bruttót. */
export function enteredAmountForBasis(row: {
  amount: number;
  amount_net?: number | null;
  amount_gross?: number | null;
  amount_basis?: string | null;
}): number {
  const basis = isAmountBasis(row.amount_basis) ? row.amount_basis : "gross";
  if (basis === "net" || basis === "black") {
    return row.amount_net ?? row.amount;
  }
  return row.amount_gross ?? row.amount;
}

/**
 * Kiadás nettó (bez DPH) összege.
 * Ha van amount_net, azt használja; különben a bruttóból számol (s DPH → bez DPH).
 * Fekete / non_vat esetén nettó = bruttó.
 */
export function expenseNetAmount(row: {
  amount?: number | null | undefined;
  amount_net?: number | null | undefined;
  amount_gross?: number | null | undefined;
  vat_rate?: number | null | undefined;
  vat_code?: string | null | undefined;
}): number {
  if (typeof row.amount_net === "number" && Number.isFinite(row.amount_net)) {
    return row.amount_net;
  }
  const gross =
    typeof row.amount_gross === "number" && Number.isFinite(row.amount_gross)
      ? row.amount_gross
      : typeof row.amount === "number" && Number.isFinite(row.amount)
        ? row.amount
        : 0;
  const code = isVatCode(row.vat_code) ? row.vat_code : vatCodeFromRate(row.vat_rate);
  return breakdownFromGross(gross, code).amount_net;
}
